/**
 * Bundle Discount Logic
 *
 * Calculates automatic discounts when qualifying products are purchased together.
 * Discounts appear as negative-price line items in the cart and on invoices.
 */

import type { CartItem } from "./types";

/**
 * HSC Science product IDs (extracted from SKUs)
 * Used for science bundle discount triggers
 */
const SCIENCE_PRODUCTS = [
  "hsc_bio",
  "hsc_chem",
  "hsc_phys",
  "hsc_eng",
  "hsc_hms",
];

/**
 * Discount rule definition
 */
export interface DiscountRule {
  id: string;
  name: string;
  description: string;
  /** Function to check if this rule is triggered based on product IDs in cart */
  triggerCheck: (productIds: Set<string>) => boolean;
  /** Product IDs that receive the discount when rule is triggered */
  targetProducts: string[];
  /** Type of discount: fixed amount in cents or percentage */
  discountType: "fixed" | "percentage";
  /** Discount value: cents for fixed, 0-100 for percentage */
  discountValue: number;
  /** Which tier types to apply discount to */
  applyTo: "initial" | "additional" | "both";
}

/**
 * Bundle discount rules
 *
 * Rule 1: Standard 1 + Standard 2 Bundle
 * - Trigger: Cart has both hsc_std1 and hsc_std2
 * - Target: hsc_std1 initial tier only
 * - Discount: $100 fixed
 *
 * Rule 2: NSW Stage + SMO Bundle
 * - Trigger: Cart has any NSW Stage product AND smo_y3_9
 * - Target: All NSW Stage products (initial and additional)
 * - Discount: 50%
 *
 * Rule 3: Science Bundle (All 5)
 * - Trigger: Cart has all 5 science products (bio, chem, phys, eng, hms)
 * - Target: First matching science product (fixed discount applies once)
 * - Discount: $150 fixed
 *
 * Rule 4: Science Bundle (4 Products)
 * - Trigger: Cart has exactly 4 science products (not all 5)
 * - Target: First matching science product (fixed discount applies once)
 * - Discount: $100 fixed
 *
 * Rule 5: NSW Stage + SMO Minimum Standards Bundle
 * - Trigger: Cart has any NSW Stage product AND smo_minstd
 * - Target: smo_minstd initial tier only
 * - Discount: $100 fixed
 */
export const DISCOUNT_RULES: DiscountRule[] = [
  {
    id: "std1_std2_bundle",
    name: "Standard 1 + Standard 2 Bundle",
    description: "Save $100 when purchasing Standard 1 with Standard 2",
    triggerCheck: (ids) => ids.has("hsc_std1") && ids.has("hsc_std2"),
    targetProducts: ["hsc_std1"],
    discountType: "fixed",
    discountValue: 10000, // $100 in cents
    applyTo: "initial",
  },
  {
    id: "nsw_smo_bundle",
    name: "NSW Stage + SMO Bundle",
    description: "50% off NSW Stage Mathematics with SmarterMaths Online (Years 3-10)",
    triggerCheck: (ids) => {
      const nswProducts = [
        "nsw_stg2_slv",
        "nsw_stg3_slv",
        "nsw_stg4_slv",
        "nsw_stg5_slv",
      ];
      return nswProducts.some((p) => ids.has(p)) && ids.has("smo_y3_9");
    },
    targetProducts: ["nsw_stg2_slv", "nsw_stg3_slv", "nsw_stg4_slv", "nsw_stg5_slv"],
    discountType: "percentage",
    discountValue: 50,
    applyTo: "both",
  },
  {
    id: "science_bundle_5",
    name: "Science Bundle (All 5)",
    description: "Buy all 5 HSC Science products and save $150",
    triggerCheck: (ids) => SCIENCE_PRODUCTS.every((p) => ids.has(p)),
    targetProducts: SCIENCE_PRODUCTS,
    discountType: "fixed",
    discountValue: 15000, // $150 in cents
    applyTo: "initial",
  },
  {
    id: "science_bundle_4",
    name: "Science Bundle (4 Products)",
    description: "Buy any 4 HSC Science products and save $100",
    triggerCheck: (ids) => {
      const count = SCIENCE_PRODUCTS.filter((p) => ids.has(p)).length;
      return count === 4; // Exactly 4, not 5 (prevents double discount)
    },
    targetProducts: SCIENCE_PRODUCTS,
    discountType: "fixed",
    discountValue: 10000, // $100 in cents
    applyTo: "initial",
  },
  {
    id: "nsw_minstd_bundle",
    name: "NSW Stage + SMO Minimum Standards Bundle",
    description:
      "Save $100 on SmarterMaths Online (HSC Minimum Standards) when purchased with any NSW Mathematics (Y3-10) course",
    triggerCheck: (ids) => {
      const nswProducts = [
        "nsw_stg2_slv",
        "nsw_stg3_slv",
        "nsw_stg4_slv",
        "nsw_stg5_slv",
      ];
      return nswProducts.some((p) => ids.has(p)) && ids.has("smo_minstd");
    },
    targetProducts: ["smo_minstd"],
    discountType: "fixed",
    discountValue: 10000, // $100 in cents
    applyTo: "initial",
  },
];

/**
 * Extract the product group ID from a SKU
 *
 * Examples:
 * - "hsc_std1_initial" -> "hsc_std1"
 * - "hsc_std2_slv_initial" -> "hsc_std2"
 * - "hsc_std2_brz_additional" -> "hsc_std2"
 * - "nsw_stg4_slv_initial" -> "nsw_stg4_slv"
 * - "smo_y3_9_initial" -> "smo_y3_9"
 */
export function extractProductId(sku: string): string {
  // Handle SKUs with variant (bronze/silver) in the middle
  // Pattern: {product}_brz_{tier} or {product}_slv_{tier}
  const variantMatch = sku.match(/^(.+)_(brz|slv)_(initial|additional)$/);
  if (variantMatch) {
    // For HSC products with variants, return base product ID (without variant)
    // e.g., "hsc_std2_slv_initial" -> "hsc_std2"
    const baseProduct = variantMatch[1];
    if (baseProduct === "hsc_ext2") {
      return "hsc_ext2_slv";
    }
    // Check if this is an HSC product (has variant in middle of name)
    if (baseProduct.startsWith("hsc_")) {
      return baseProduct;
    }
    // For NSW products, keep the _slv suffix as it's part of the product ID
    // e.g., "nsw_stg4_slv_initial" -> "nsw_stg4_slv"
    return `${baseProduct}_slv`;
  }

  // Handle SKUs without variant
  // Pattern: {product}_{tier}
  const simpleMatch = sku.match(/^(.+)_(initial|additional)$/);
  if (simpleMatch) {
    return simpleMatch[1];
  }

  // Fallback: return as-is
  return sku;
}

/**
 * Determine the tier type from a SKU
 */
export function extractTierType(sku: string): "initial" | "additional" | null {
  if (sku.includes("_initial")) return "initial";
  if (sku.includes("_additional")) return "additional";
  return null;
}

/**
 * Calculate discount line items for a set of cart items
 *
 * @param items Regular cart items (not including any existing discounts)
 * @returns Array of discount line items with negative prices
 */
export function calculateDiscounts(items: CartItem[]): CartItem[] {
  // Filter out any existing discount items
  const regularItems = items.filter((item) => !item.isDiscount);

  // Get unique product IDs in cart
  const productIds = new Set(
    regularItems.map((item) => extractProductId(item.supplierPartId))
  );

  const discounts: CartItem[] = [];
  const appliedFixedRules = new Set<string>(); // Track fixed rules that have been applied

  for (const rule of DISCOUNT_RULES) {
    // Check if this rule is triggered
    if (!rule.triggerCheck(productIds)) {
      continue;
    }

    // Find items that qualify for this discount
    for (const item of regularItems) {
      const productId = extractProductId(item.supplierPartId);

      // Check if this product is a target for the discount
      if (!rule.targetProducts.includes(productId)) {
        continue;
      }

      // Check if this tier type qualifies
      const tierType = extractTierType(item.supplierPartId);
      if (tierType && rule.applyTo !== "both" && rule.applyTo !== tierType) {
        continue;
      }

      // For fixed discounts, only apply once per rule (first matching item wins)
      if (rule.discountType === "fixed" && appliedFixedRules.has(rule.id)) {
        continue;
      }

      // Calculate discount amount
      let discountCents: number;
      if (rule.discountType === "fixed") {
        // Fixed discount (only apply once per rule, regardless of how many products match)
        discountCents = rule.discountValue;
        appliedFixedRules.add(rule.id);
      } else {
        // Percentage discount (apply to total line value)
        discountCents = Math.round(
          (item.unitPriceExGstCents * item.quantity * rule.discountValue) / 100
        );
      }

      // Create discount line item
      discounts.push({
        supplierPartId: `DISC-${rule.id}-${item.supplierPartId}`,
        name: `Discount - ${rule.name}`,
        description: rule.description,
        unitPriceExGstCents: -discountCents, // Negative for discount
        quantity: 1,
        uom: "EA",
        isDiscount: true,
        discountRuleId: rule.id,
        appliedToSku: item.supplierPartId,
      });
    }
  }

  return discounts;
}

/**
 * Check if an item is a discount line item
 */
export function isDiscountItem(item: CartItem): boolean {
  return item.isDiscount === true;
}

/**
 * Discount hints to display in product sections
 * Maps subject ID to discount description text
 */
export const SUBJECT_DISCOUNT_HINTS: Record<string, string> = {
  hsc_science: "Bundle discounts: $100 off any 4 science courses, $150 off all 5 science courses",
  hsc_mathematics: "Bundle discount: $100 off when purchasing Standard 1 with Standard 2",
  nsw_mathematics: "Bundle discount: 50% off all NSW MATHEMATICS (Y3-10) - TEACHER EDITION courses when purchased with SmarterMaths Online (Years 3-10)",
  smartermaths_online:
    "Bundle Discount: $100 off SmarterMaths Online (HSC Minimum Standards) when purchased with any NSW MATHEMATICS (Y3-10) - TEACHER EDITION course",
};
